/**
 * STEINBERG THEME — Main JavaScript
 * Handles: Cart drawer, ATC, qty controls, compatibility checker, toasts, tabs
 */

/* ═══════════════════════════════════════
   UTILITY
═══════════════════════════════════════ */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn) => el && el.addEventListener(ev, fn);

/* ═══════════════════════════════════════
   TOAST NOTIFICATIONS
═══════════════════════════════════════ */
const Toast = {
  container: null,

  init() {
    this.container = document.createElement('div');
    this.container.className = 'toast-container';
    document.body.appendChild(this.container);
  },

  show(title, msg = '', type = 'success', duration = 3500) {
    const icons = { success: '✓', error: '✕', info: '⚡' };
    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.innerHTML = `
      <div class="toast__icon">${icons[type] || '●'}</div>
      <div class="toast__msg">
        <strong>${title}</strong>
        ${msg ? `<span>${msg}</span>` : ''}
      </div>
    `;
    this.container.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(20px)';
      toast.style.transition = 'all 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }
};

/* ═══════════════════════════════════════
   CART STATE
═══════════════════════════════════════ */
const Cart = {
  data: { items: [], item_count: 0, total_price: 0 },

  async fetch() {
    try {
      const res = await fetch('/cart.js');
      this.data = await res.json();
      this.updateUI();
      return this.data;
    } catch(e) { console.error('Cart fetch error:', e); }
  },

  async addItem(variantId, quantity = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      if (!res.ok) throw new Error('Failed to add item');
      await this.fetch();
      CartDrawer.open();
      Toast.show('Added to Cart', '', 'success');
      return await res.json();
    } catch(e) {
      Toast.show('Error', 'Could not add item to cart.', 'error');
      console.error(e);
    }
  },

  async updateItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      await this.fetch();
    } catch(e) { console.error(e); }
  },

  async removeItem(key) {
    await this.updateItem(key, 0);
    Toast.show('Item removed', '', 'info');
  },

  updateUI() {
    // Cart count badge
    $$('.cart-count').forEach(el => {
      el.textContent = this.data.item_count;
      el.style.display = this.data.item_count > 0 ? 'flex' : 'none';
    });

    // Cart drawer
    CartDrawer.render(this.data);
  },

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
};

/* ═══════════════════════════════════════
   CART DRAWER
═══════════════════════════════════════ */
const CartDrawer = {
  drawer: null,
  overlay: null,

  init() {
    this.drawer = $('.cart-drawer');
    this.overlay = $('.cart-overlay');
    if (!this.drawer) return;

    on($('.cart-drawer__close'), 'click', () => this.close());
    on(this.overlay, 'click', () => this.close());
    on(document, 'keydown', e => { if (e.key === 'Escape') this.close(); });
  },

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('open');
    this.overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('open');
    this.overlay.classList.remove('open');
    document.body.style.overflow = '';
  },

  render(cartData) {
    const body = $('.cart-drawer__body');
    if (!body) return;

    if (cartData.item_count === 0) {
      body.innerHTML = `
        <div style="text-align:center;padding:48px 20px;color:var(--mist);">
          <div style="font-size:48px;margin-bottom:16px;opacity:0.3;">🛒</div>
          <div style="font-family:var(--font-display);font-size:18px;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin-bottom:8px;">Cart is Empty</div>
          <div style="font-size:13px;">Add some filters or brakes to get started.</div>
        </div>`;
    } else {
      body.innerHTML = cartData.items.map(item => `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__img">
            ${item.featured_image
              ? `<img src="${item.featured_image.url}?width=160" alt="${(item.product_title||'').replace(/"/g,'&quot;')}" style="width:100%;height:100%;object-fit:cover;">`
              : `<div class="pc-img-art" style="width:48px;height:64px;"><div class="pc-img-art-line"></div><div class="pc-img-art-line"></div><div class="pc-img-art-line"></div></div>`}
          </div>
          <div class="cart-item__info">
            <a href="${item.url}" class="cart-item__name">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
            <div class="cart-item__sku">SKU: ${item.sku || item.variant_id}</div>
            <div class="cart-item__bottom">
              <div class="cart-item__price">${Cart.formatMoney(item.final_line_price)}</div>
              <div class="cart-item__qty">
                <button class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}">−</button>
                <span>${item.quantity}</span>
                <button class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}">+</button>
                <button class="cart-item__qty-btn" data-action="remove" data-key="${item.key}" style="margin-left:4px;color:var(--red);" title="Remove">✕</button>
              </div>
            </div>
          </div>
        </div>`).join('');
    }

    // Subtotal
    const subtotalEl = $('.cart-subtotal__price');
    if (subtotalEl) subtotalEl.textContent = Cart.formatMoney(cartData.total_price);

    // Show/hide footer
    const footer = document.getElementById('cart-drawer-footer');
    if (footer) footer.style.display = cartData.item_count > 0 ? 'block' : 'none';

    // Update checkout URL to always point to /checkout
    const checkoutBtn = footer ? footer.querySelector('a[href*="checkout"], a[href*="/cart"]') : null;
    if (checkoutBtn && window.theme && window.theme.checkoutUrl) {
      checkoutBtn.href = window.theme.checkoutUrl;
    }

    // Wholesale note
    const threshold = parseInt(window.theme?.wholesaleThreshold || 300) * 100;
    const wholesaleNote = $('.cart-wholesale-note');
    if (wholesaleNote) {
      wholesaleNote.style.display = (cartData.total_price >= threshold && cartData.item_count > 0) ? 'block' : 'none';
    }

    // Free shipping progress
    this.updateShippingBar(cartData.total_price);

    // Bind qty buttons
    $$('.cart-item__qty-btn', body).forEach(btn => {
      on(btn, 'click', () => {
        const key = btn.dataset.key;
        const qty = parseInt(btn.dataset.qty);
        const action = btn.dataset.action;
        if (action === 'increase') Cart.updateItem(key, qty + 1);
        else if (action === 'decrease' && qty > 1) Cart.updateItem(key, qty - 1);
        else if (action === 'decrease' && qty === 1) Cart.removeItem(key);
        else if (action === 'remove') Cart.removeItem(key);
      });
    });
  },

  updateShippingBar(totalCents) {
    const bar = document.getElementById('shipping-progress-bar');
    const msg = document.getElementById('shipping-progress-msg');
    if (!bar) return;
    const threshold = parseInt(window.theme?.freeShippingThreshold || 500) * 100;
    const pct = Math.min((totalCents / threshold) * 100, 100);
    bar.style.width = pct + '%';
    if (msg) {
      const remaining = Cart.formatMoney(Math.max(0, threshold - totalCents));
      msg.textContent = totalCents >= threshold
        ? '🎉 You qualify for free shipping!'
        : `${remaining} away from free shipping`;
    }
  }
};

/* ═══════════════════════════════════════
   ADD TO CART BUTTONS
═══════════════════════════════════════ */
function initATC() {
  on(document, 'click', async (e) => {
    const btn = e.target.closest('[data-atc]');
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId || btn.dataset.atc;
    if (!variantId) return;

    // Get qty if available
    const qtyEl = btn.closest('[data-product-form]')?.querySelector('.qty-val');
    const qty = qtyEl ? parseInt(qtyEl.textContent) : 1;

    const orig = btn.innerHTML;
    btn.innerHTML = '<span class="spinner"></span>';
    btn.disabled = true;

    await Cart.addItem(variantId, qty);

    btn.innerHTML = '✓ Added';
    btn.classList.add('added');
    setTimeout(() => {
      btn.innerHTML = orig;
      btn.classList.remove('added');
      btn.disabled = false;
    }, 1800);
  });
}

/* ═══════════════════════════════════════
   QTY CONTROLS
═══════════════════════════════════════ */
function initQtyControls() {
  on(document, 'click', (e) => {
    const btn = e.target.closest('.qty-btn');
    if (!btn) return;

    const val = btn.closest('.qty-ctrl')?.querySelector('.qty-val');
    if (!val) return;

    let n = parseInt(val.textContent) || 1;
    if (btn.textContent.trim() === '+') n = Math.min(n + 1, 999);
    else n = Math.max(n - 1, 1);
    val.textContent = n;
  });
}

/* ═══════════════════════════════════════
   PDP TABS
═══════════════════════════════════════ */
function initPDPTabs() {
  on(document, 'click', (e) => {
    const tab = e.target.closest('.pdp-tab');
    if (!tab) return;

    const container = tab.closest('.pdp-info') || tab.closest('.pdp-tabs-container');
    if (!container) return;

    $$('.pdp-tab', container).forEach(t => t.classList.remove('active'));
    tab.classList.add('active');

    const tabName = tab.dataset.tab;
    $$('.pdp-tab-panel', container).forEach(p => {
      p.style.display = p.dataset.panel === tabName ? 'block' : 'none';
    });
  });
}

/* ═══════════════════════════════════════
   FILTER SIDEBAR TOGGLES
═══════════════════════════════════════ */
function initFilterSidebar() {
  // ── Group toggle (accordion) ──
  on(document, 'click', (e) => {
    const hd = e.target.closest('.fs-group-hd');
    if (!hd) return;
    const group = hd.closest('.fs-group');
    if (!group) return;
    group.classList.toggle('open');
    const toggle = hd.querySelector('.fs-toggle');
    if (toggle) toggle.textContent = group.classList.contains('open') ? '−' : '+';
  });

  // On desktop: open all groups by default on load
  function initGroupStates() {
    if (window.innerWidth >= 1025) {
      $$('.fs-group').forEach(g => g.classList.add('open'));
    }
  }
  initGroupStates();
  window.addEventListener('resize', initGroupStates);

  // ── Mobile filter toggle button ──
  // Inject "Show Filters" button above sidebar on mobile
  on(document, 'click', (e) => {
    const btn = e.target.closest('.mobile-filter-toggle');
    if (!btn) return;
    const sidebar = btn.nextElementSibling;
    if (!sidebar || !sidebar.classList.contains('filter-sidebar')) return;
    const isHidden = sidebar.classList.contains('mobile-hidden');
    sidebar.classList.toggle('mobile-hidden', !isHidden);
    btn.querySelector('.mft-label').textContent = isHidden ? 'Hide Filters' : 'Show Filters';
  });

  // Insert mobile filter toggle if not present
  const sidebar = $('.filter-sidebar');
  if (sidebar && !$('.mobile-filter-toggle')) {
    const activeCount = sidebar.querySelectorAll('.fs-tag.active, .fs-box.checked').length;
    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'mobile-filter-toggle';
    toggleBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg><span class="mft-label">Show Filters</span>${activeCount > 0 ? `<span class="mft-count">${activeCount}</span>` : ''}`;
    sidebar.classList.add('mobile-hidden');
    sidebar.parentNode.insertBefore(toggleBtn, sidebar);
  }

  // ── Filter tag clicks ──
  on(document, 'click', (e) => {
    const tag = e.target.closest('.fs-tag[data-tag]');
    if (!tag) return;
    // Tags with data-tag are real Shopify tag links — let them navigate
    // Tags without are demo/UI only — toggle visual state
  });

  // ── Checkboxes (visual toggle only — real filtering via URL) ──
  on(document, 'click', (e) => {
    const check = e.target.closest('.fs-check');
    if (!check) return;
    const box = check.querySelector('.fs-box');
    if (box) box.classList.toggle('checked');
  });
}

/* ═══════════════════════════════════════
   COMPATIBILITY CHECKER
═══════════════════════════════════════ */
function initCompatChecker() {
  const form = $('.compat-form');
  if (!form) return;

  on(form, 'submit', (e) => {
    e.preventDefault();
    const make  = form.querySelector('[name="make"]')?.value;
    const model = form.querySelector('[name="model"]')?.value;
    const year  = form.querySelector('[name="year"]')?.value;

    if (!make || !model) {
      Toast.show('Please select make & model', '', 'info');
      return;
    }

    // Redirect to collection filtered by vehicle
    const url = new URL('/collections/all', window.location.origin);
    url.searchParams.set('filter.p.m.custom.vehicle_make', make);
    url.searchParams.set('filter.p.m.custom.vehicle_model', model);
    if (year) url.searchParams.set('filter.p.m.custom.vehicle_year', year);
    window.location.href = url.toString();
  });

  // Chain selects: model depends on make
  const makeSelect  = form.querySelector('[name="make"]');
  const modelSelect = form.querySelector('[name="model"]');

  if (makeSelect && modelSelect) {
    on(makeSelect, 'change', () => {
      const make = makeSelect.value;
      // Populated server-side via Liquid, but enable/disable here
      modelSelect.disabled = !make;
    });
  }
}

/* ═══════════════════════════════════════
   CART ICON CLICK → OPEN DRAWER
═══════════════════════════════════════ */
function initCartIcon() {
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-open-cart]');
    if (!btn) return;
    e.preventDefault();
    CartDrawer.open();
  });
}

/* ═══════════════════════════════════════
   STICKY HEADER SHRINK
═══════════════════════════════════════ */
function initStickyHeader() {
  const header = $('.site-header');
  if (!header) return;

  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    // Optionally add shadow when scrolled
    header.style.boxShadow = y > 10 ? '0 2px 20px rgba(0,0,0,0.4)' : '';
    lastY = y;
  }, { passive: true });
}

/* ═══════════════════════════════════════
   SEARCH SUGGESTIONS (basic)
═══════════════════════════════════════ */
function initSearch() {
  const input = $('.header-search__input');
  if (!input) return;

  let timer;
  on(input, 'input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) return;
    timer = setTimeout(() => {
      // Shopify predictive search API
      fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`)
        .then(r => r.json())
        .then(data => {
          // Results handled by Liquid predictive search section if implemented
          // This is a placeholder for custom search dropdown
        }).catch(() => {});
    }, 300);
  });

  on(input, 'keydown', (e) => {
    if (e.key === 'Enter') {
      window.location.href = `/search?q=${encodeURIComponent(input.value.trim())}`;
    }
  });
}

/* ═══════════════════════════════════════
   MOBILE MENU
   Single handler — duplicate removed from
   mobile-nav.liquid snippet
═══════════════════════════════════════ */
function initMobileMenu() {
  const toggle  = document.querySelector('.mobile-menu-toggle');
  const nav     = document.getElementById('mobile-nav-drawer');
  const overlay = document.getElementById('mobile-nav-overlay');

  if (!toggle || !nav) return;

  function openNav() {
    nav.classList.add('open');
    nav.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    toggle.setAttribute('aria-expanded', 'true');
  }

  function closeNav() {
    nav.classList.remove('open');
    nav.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
    toggle.setAttribute('aria-expanded', 'false');
  }

  // Toggle button
  toggle.addEventListener('click', () => {
    nav.classList.contains('open') ? closeNav() : openNav();
  });

  // Overlay background click
  if (overlay) overlay.addEventListener('click', closeNav);

  // Close button inside drawer
  nav.addEventListener('click', (e) => {
    if (e.target.closest('[data-close-mobile-nav]')) closeNav();
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('open')) closeNav();
  });

  // Accordion — nav group toggles
  nav.querySelectorAll('.mobile-nav__group-hd').forEach(btn => {
    btn.addEventListener('click', function() {
      const body   = this.nextElementSibling;
      const isOpen = !body.hidden;
      body.hidden  = isOpen;
      this.setAttribute('aria-expanded', String(!isOpen));
      const caret = this.querySelector('.mobile-nav__caret');
      if (caret) caret.textContent = isOpen ? '▾' : '▴';
    });
  });
}

/* ═══════════════════════════════════════
   PRODUCT GALLERY
═══════════════════════════════════════ */
function initProductGallery() {
  on(document, 'click', (e) => {
    const thumb = e.target.closest('.pdp-thumb');
    if (!thumb) return;

    const gallery = thumb.closest('.pdp-gallery');
    if (!gallery) return;

    $$('.pdp-thumb', gallery).forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');

    const mainImg = gallery.querySelector('.pdp-main-img img');
    const src = thumb.dataset.src;
    if (mainImg && src) mainImg.src = src;
  });
}

/* ═══════════════════════════════════════
   NEWSLETTER FORM
═══════════════════════════════════════ */
function initNewsletterForms() {
  $$('.footer-newsletter-form').forEach(form => {
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const btn = form.querySelector('button[type="submit"]');
      if (!input?.value) return;

      const orig = btn.textContent;
      btn.textContent = '…';
      btn.disabled = true;

      // Shopify customer newsletter subscription
      try {
        await fetch('/contact#newsletter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            'form_type': 'customer',
            'utf8': '✓',
            'customer[email]': input.value,
            'contact[tags]': 'newsletter,fleet-updates'
          })
        });
        btn.textContent = '✓ Subscribed';
        input.value = '';
        Toast.show('Subscribed!', 'You\'ll receive fleet updates from Steinberg.', 'success');
      } catch {
        btn.textContent = orig;
        btn.disabled = false;
        Toast.show('Error', 'Could not subscribe. Please try again.', 'error');
      }
    });
  });
}

/* ═══════════════════════════════════════
   WHOLESALE FORM
═══════════════════════════════════════ */
function initWholesaleForm() {
  const form = $('#wholesale-application-form');
  if (!form) return;

  on(form, 'submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const orig = btn.textContent;
    btn.textContent = 'Submitting…';
    btn.disabled = true;

    const data = new FormData(form);
    data.append('form_type', 'contact');
    data.append('utf8', '✓');

    try {
      const res = await fetch('/contact#wholesale-form', {
        method: 'POST',
        body: new URLSearchParams(data)
      });

      if (res.ok) {
        form.innerHTML = `
          <div style="text-align:center;padding:48px 20px;">
            <div style="font-size:56px;margin-bottom:20px;">✅</div>
            <div class="section-title" style="margin-bottom:12px;">Application Received</div>
            <p style="color:var(--mist);font-size:15px;line-height:1.7;">Thank you for your interest in Steinberg wholesale.<br>Our team will contact you within <strong style="color:var(--white);">1 business day</strong>.</p>
          </div>`;
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      btn.textContent = orig;
      btn.disabled = false;
      Toast.show('Error', 'Submission failed. Email us at info@steinbergus.com', 'error');
    }
  });
}

/* ═══════════════════════════════════════
   SCROLL REVEAL (lightweight)
═══════════════════════════════════════ */
function initScrollReveal() {
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  $$('[data-reveal]').forEach(el => observer.observe(el));
}

/* ═══════════════════════════════════════
   INIT
═══════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  Toast.init();
  Cart.fetch();
  CartDrawer.init();
  initATC();
  initQtyControls();
  initPDPTabs();
  initFilterSidebar();
  initCompatChecker();
  initCartIcon();
  initStickyHeader();
  initSearch();
  initMobileMenu();
  initProductGallery();
  initNewsletterForms();
  initWholesaleForm();
  initScrollReveal();
});
